import { LOADING_STATUS } from "../../common/constants";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { RootState } from "../../app/store";

export interface OwnerAddress {
  id: string;
  countryId?: string;
  isPrimary: boolean;
  postCity?: string;
  postCode?: string;
  postStreetAddress?: string;
}

export interface OwnerEmails {
  id: string;
  emailAddress?: string;
  isBookingNoteEmail: boolean;
  isPrimary: boolean;
}

export interface OwnerPhones {
  id: string;
  phoneNumber?: string;
  bookingNoteBySms: boolean;
  isPrimary: boolean;
}

export interface OwnerInfo {
  id?: string;
  firstName?: string;
  lastName?: string;
  originalFirstName?: string;
  originalLastName?: string;
  birthDay?: string;
  birthPlace?: string;
  businessIdentityCode?: string;
  addresses?: OwnerAddress[];
  emails?: OwnerEmails[];
  phones?: OwnerPhones[];
  postCity?: string;
  postCode?: string;
  postStreetAddress?: string;
  countryId?: string;
  phoneNumber?: string;
  email?: string;
  emailForBookings?: string;
}
const initialState = {
  ownerInfo: {
    id: null,
    firstName: null,
    lastName: null,
    originalFirstName: null,
    originalLastName: null,
    birthDay: null,
    birthPlace: null,
    businessIdentityCode: null,
    postCity: null,
    postCode: null,
    postStreetAddress: null,
    countryId: null,
    phoneNumber: null,
    email: null,
    emailForBookings: null,
  },
  ownerStatus: LOADING_STATUS.LOADING,
};

export const getAccountDetailsFromDbAsync = createAsyncThunk(
  "users/getAcountDetailsFromDbAsync",
  async (accountId: string, { rejectWithValue }) => {
    const ownerInfo = await apiService.get(
      API_ENDPOINTS.IDENTITY.GET_ACCOUNT_DETAILS + "?accountId=" + accountId,
    );
    ownerInfo.originalFirstName = ownerInfo.firstName;
    ownerInfo.originalLastName = ownerInfo.lastName;
    if (ownerInfo.addresses != null && ownerInfo.addresses.length > 0) {
      let isFirstAddress = true;
      ownerInfo.addresses.forEach((address) => {
        if (isFirstAddress || address.isPrimary) {
          isFirstAddress = false;
          ownerInfo.postCity = address.postCity;
          ownerInfo.postCode = address.postCode;
          ownerInfo.postStreetAddress = address.postStreetAddress;
          ownerInfo.countryId = address.countryId;
        }
      });
    }
    if (ownerInfo.phones != null && ownerInfo.phones.length > 0) {
      let isFirstPhoneNumber = true;
      ownerInfo.phones.forEach((phoneNumber) => {
        if (isFirstPhoneNumber || phoneNumber.isPrimary) {
          isFirstPhoneNumber = false;
          ownerInfo.phoneNumber = phoneNumber.phoneNumber;
        }
      });
    }
    if (ownerInfo.emails != null && ownerInfo.emails.length > 0) {
      let isFirstEmail = true;
      ownerInfo.emails.forEach((email) => {
        if (isFirstEmail || email.isPrimary) {
          isFirstEmail = false;
          ownerInfo.email = email.emailAddress;
        }
        if (email.isBookingNoteEmail) {
          ownerInfo.emailForBookings = email.emailAddress;
        }
      });
    }
    return { ownerInfo };
  },
);

export const updateOwnerAsync = createAsyncThunk(
  "users/updateOwner",
  async (ownerInfo: OwnerInfo, { rejectWithValue }) => {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.IDENTITY.SET_ACCOUNT_DETAILS,
        ownerInfo,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const userSlice = createSlice({
  name: "owner",
  initialState,
  reducers: {
    changeStateOwnerDateOfBirth: (state, action) => {
      const { dateOfBirthValue } = action.payload;
      state.ownerInfo.birthDay = dateOfBirthValue;
    },
    changeStateOwnerData: (state, action) => {
      const modifiedOwnerData = action.payload.ownerData;
      console.log(modifiedOwnerData);
      state.ownerInfo = modifiedOwnerData;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountDetailsFromDbAsync.pending, (state) => {
        state.ownerStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccountDetailsFromDbAsync.fulfilled, (state, action) => {
        state.ownerInfo = action.payload.ownerInfo;
        state.ownerStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getAccountDetailsFromDbAsync.rejected, (state) => {
        state.ownerInfo = null;
        state.ownerStatus = LOADING_STATUS.FAILED;
      });
  },
});
export const selectOwnerInfo = (state: RootState) => state.owner.ownerInfo;
export const { changeStateOwnerDateOfBirth } = userSlice.actions;
export const { changeStateOwnerData } = userSlice.actions;
export const selectOwnerDetailsLoadingStatus = (state: RootState) =>
  state.owner.ownerStatus;

export default userSlice.reducer;
